import { useState } from 'react';
import { Printer, ScanLine, Shield, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { getPreferredPrintMode, printTestLabel, setPreferredPrintMode, type PrintMode } from '@/shared/print/zebra';

export function ConfiguracoesPage() {
  const [testState, setTestState] = useState<'idle' | 'printing' | 'ok' | 'error'>('idle');
  const [testError, setTestError] = useState<string | null>(null);
  const [printMode, setPrintMode] = useState<PrintMode>(getPreferredPrintMode);

  function updateMode(mode: PrintMode) {
    setPrintMode(mode);
    setPreferredPrintMode(mode);
  }

  async function runTest() {
    setTestState('printing');
    setTestError(null);
    try {
      await printTestLabel();
      setTestState('ok');
    } catch (e) {
      setTestError(e instanceof Error ? e.message : String(e));
      setTestState('error');
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader title="Impressora Zebra ZD220" subtitle="Configuração de etiquetas térmicas 50×30 mm" action={<Badge tone="brand">ZPL II · 203 dpi</Badge>} />
        <CardBody className="space-y-4">
          <div className="rounded border border-neutral-200 p-3 dark:border-neutral-700">
            <p className="mb-2 text-sm font-medium text-neutral-800 dark:text-neutral-100">Modo de impressão padrão</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={printMode === 'zebra' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => updateMode('zebra')}
              >
                Zebra (ZPL)
              </Button>
              <Button
                variant={printMode === 'common' ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => updateMode('common')}
              >
                Impressora comum
              </Button>
            </div>
            <p className="mt-2 text-xs text-neutral-500">
              Em ambos os modos, a aplicação mostra pré-visualização com QR e barcode antes de imprimir.
            </p>
          </div>

          <ol className="ml-4 list-decimal space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
            <li>Conecte a ZD220 via USB; o Windows reconhece como impressora térmica.</li>
            <li>Em <strong>Painel de Controle → Dispositivos e Impressoras → Adicionar Impressora</strong>, escolha
              <strong> "A impressora desejada não está na lista" → "Adicionar usando endereço TCP/IP" </strong>
              (ou USB001), e selecione o driver <strong>Generic / Text Only</strong>.</li>
            <li>Renomeie a impressora para <code className="font-mono">Zebra-ICTUS</code> e marque como padrão.</li>
            <li>Carregue rolo contínuo 50×30 mm e calibre segurando o botão FEED por ~3s.</li>
            <li>No Zebra Setup Utilities, defina mídia como <strong>Contínua</strong> e largura <strong>400 dots</strong>.</li>
            <li>Permita pop-ups deste site no navegador (ícone do cadeado na barra de endereço).</li>
            <li>Use o botão abaixo para imprimir uma etiqueta de teste.</li>
          </ol>

          <div className="flex items-center gap-3">
            <Button size="touch" leadingIcon={<Printer className="h-5 w-5" />} onClick={runTest} loading={testState === 'printing'}>
              Imprimir etiqueta de teste
            </Button>
            {testState === 'ok' ? (
              <span className="inline-flex items-center gap-1 text-sm text-success"><CheckCircle2 className="h-4 w-4" /> Enviado para a impressora</span>
            ) : null}
            {testState === 'error' ? (
              <span className="inline-flex items-center gap-1 text-sm text-danger"><AlertTriangle className="h-4 w-4" /> {testError}</span>
            ) : null}
          </div>

          <details className="rounded border border-neutral-200 p-3 text-xs dark:border-neutral-700">
            <summary className="cursor-pointer font-medium text-neutral-700 dark:text-neutral-200">Resolução de problemas</summary>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-neutral-600 dark:text-neutral-400">
              <li>Etiqueta sai em branco: confirme se o driver é <strong>Generic / Text Only</strong>, não o driver Zebra ZDesigner.</li>
              <li>Cuspe vazio depois da impressão: defina a mídia como <strong>Contínua</strong> e calibre o sensor.</li>
              <li>QR Code não aparece: verifique a firmware da ZD220 (precisa ZPL II — todas as ZD220 trazem por padrão).</li>
              <li>Pop-up bloqueado: clique no ícone bloqueado na barra e permita sempre.</li>
            </ul>
          </details>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Leitor de QR Code / Código de Barras" subtitle="Scanners HID (USB) — qualquer marca compatível com emulação de teclado" action={<Badge tone="brand">HID Keyboard</Badge>} />
        <CardBody className="space-y-4">
          <p className="text-sm text-neutral-700 dark:text-neutral-300">
            O ICTUS detecta automaticamente leitores 1D (Code 128, EAN, QR-EAN) e 2D (QR Code, DataMatrix, PDF417)
            conectados via USB. O leitor deve estar em modo <strong>Keyboard Emulation (HID)</strong> e configurado
            com terminador <strong>CR/Enter</strong> (padrão de fábrica da maioria dos modelos).
          </p>
          <div className="rounded border border-neutral-200 bg-neutral-50 p-3 text-xs dark:border-neutral-700 dark:bg-neutral-900">
            <strong className="block">Modelos recomendados:</strong>
            <ul className="mt-1 list-disc pl-5 text-neutral-600 dark:text-neutral-400">
              <li>Honeywell Voyager 1450g (1D + 2D, melhor custo-benefício hospitalar)</li>
              <li>Zebra DS2208 (1D + 2D, robusto, mesmo ecossistema da ZD220)</li>
              <li>Datalogic QuickScan QD2430 (1D + 2D, leitura excelente em DataMatrix gravado a laser)</li>
            </ul>
          </div>
          <div className="rounded border border-neutral-200 bg-neutral-50 p-3 text-xs dark:border-neutral-700 dark:bg-neutral-900">
            <strong className="block">Configuração obrigatória:</strong>
            <ul className="mt-1 list-disc pl-5 text-neutral-600 dark:text-neutral-400">
              <li>Modo USB HID Keyboard (não Virtual COM)</li>
              <li>Sufixo: CR (Enter) — habilitado por padrão</li>
              <li>Layout do teclado: US (evita conflito com acentos)</li>
              <li>Beep de confirmação: ligado</li>
            </ul>
          </div>
          <div className="rounded border border-info-bg p-3 text-sm text-neutral-700 dark:text-neutral-300">
            <ScanLine className="mr-1 inline h-4 w-4 text-info" />
            Para testar, abra qualquer tela operacional (Expurgo, Preparo, Arsenal) e bipe um código.
            O sistema reage com beep visual e dispara a transição da FSM correspondente.
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Segurança & Secrets" subtitle="Chaves de assinatura no Cloudflare Workers" action={<Shield className="h-5 w-5 text-warning" />} />
        <CardBody className="space-y-2 text-sm">
          <p className="text-neutral-700 dark:text-neutral-300">
            JWT e webhooks ERP são assinados com secrets armazenados via <code>wrangler secret put</code>.
            Para rotacioná-los (recomendado a cada 90 dias):
          </p>
          <pre className="overflow-auto rounded bg-neutral-50 p-2 text-xs dark:bg-neutral-900">
{`cd worker
npx wrangler secret put JWT_SECRET
npx wrangler secret put ERP_WEBHOOK_SIGNING_KEY`}
          </pre>
          <p className="text-xs text-neutral-500">
            Após rotação, todas as sessões ativas são invalidadas — usuários precisam relogar.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Conformidade" subtitle="RDC 15 / ISO 17665 / LGPD" />
        <CardBody className="space-y-2 text-sm text-neutral-700 dark:text-neutral-300">
          <p>O ICTUS atende:</p>
          <ul className="ml-4 list-disc space-y-1">
            <li><strong>RDC 15/2012 ANVISA</strong> — rastreabilidade ciclo-a-ciclo, Bowie-Dick diário, indicador biológico, lote de processamento.</li>
            <li><strong>ISO 17665</strong> — esterilização por vapor: liberação paramétrica documentada com TempºC, PSI, duração.</li>
            <li><strong>NBR ISO 11140</strong> — registro de indicadores químicos tipo 5/6 em cada lote.</li>
            <li><strong>LGPD</strong> — trilha de auditoria imutável, dados de paciente em vínculo cirurgia opcional.</li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
}
